import { CSSProperties, useState } from 'react';
import {
  DefaultNodeOptions,
  getNode,
  isParentNode,
  isValueNode,
  ParentNode,
  widget,
  WidgetPlugin,
} from '@sagold/react-json-editor';

import { FormField } from '../../../components/formfield/FormField';

import styles from './tabWidget.module.scss';

function Tab({
  children,
  isActive,
  onClick,
}: {
  children: React.ReactNode | React.ReactNode[];
  isActive: boolean;
  onClick: () => void;
}) {
  const style: CSSProperties = {
    cursor: isActive ? 'default' : 'pointer',
    padding: '2px 8px',
    borderBottom: isActive ? '2px solid black' : '2px solid #ccc',
  };
  return (
    <div className="portal-tab" onClick={onClick} style={style}>
      {children}
    </div>
  );
}

export type TabWidgetOptions = DefaultNodeOptions<{
  /** retrieve tab title via json-pointer from array child to tab-title value.
   * e.g. ShareName or "/header/title". Defaults to property name
   */
  tabTitle?: string;
}>;

export const TabWidget = widget<ParentNode<TabWidgetOptions>, unknown>(
  ({ node, editor, options }) => {
    const [tabIndex, setTabIndex] = useState(0);
    const childNode = node.children[tabIndex];
    if (childNode == null) {
      return null;
    }
    const Widget = editor.getWidget(childNode);
    return (
      <FormField
        widgetType={node.type}
        node={node}
        options={options}
        className={['portal-tabs', ...(options.classNames ?? [])].join(' ')}
      >
        <div className={styles['portal-tabs__tabs']}>
          {node.children.map((tab, index) => {
            const titleNode =
              options.tabTitle && getNode(tab, options.tabTitle);
            let title = tab.property;
            if (isValueNode(titleNode) && typeof titleNode.value === 'string') {
              title = titleNode.value;
            }
            return (
              <Tab
                key={tab.id}
                onClick={() => setTabIndex(index)}
                isActive={index === tabIndex}
              >
                {title}
              </Tab>
            );
          })}
        </div>
        <div className="portal-tabs__content">
          <Widget node={childNode} editor={editor} />
        </div>
      </FormField>
    );
  }
);

export const TabWidgetPlugin: WidgetPlugin = {
  id: 'tab-widget',
  use: (node) => node.schema.options?.widget === 'tabs' && isParentNode(node),
  Widget: TabWidget,
};
